type whereType = 'UNIVERSITY' | 'ADDRESS' | 'SUBWAY_STATION' | 'COMMERCIAL_AREA' | 'MAP_BOUNDS';

/**
 * @description
 * 도메인 `whereType` 값을 Google Places API의 `type` 문자열로 변환합니다.
 * `UNIVERSITY` → `university`, `ADDRESS` → `establishment`, `SUBWAY_STATION` → `subway_station`, `COMMERCIAL_AREA` → `establishment`로 매핑됩니다.
 * 정의되지 않은 값은 기본값으로 `establishment`를 반환합니다.
 *
 * @param whereType - 도메인 레벨 위치 유형
 * @returns Google Places API `TextSearchRequest.type`에 사용될 문자열
 * @example
 * ```ts
 * convertWhereTypeToGooglePlaceType('UNIVERSITY'); // 'university'
 * convertWhereTypeToGooglePlaceType('ADDRESS'); // 'establishment'
 * convertWhereTypeToGooglePlaceType('COMMERCIAL_AREA'); // 'establishment'
 * ```
 */
export const convertWhereTypeToGooglePlaceType = (whereType: whereType): string => {
  switch (whereType) {
    case 'UNIVERSITY':
      return 'university';
    case 'ADDRESS':
      return 'establishment';
    case 'SUBWAY_STATION':
      return 'subway_station';
    case 'COMMERCIAL_AREA':
      return 'establishment';
    default:
      return 'establishment';
  }
};

/**
 * @description
 * Google Places Text Search API를 사용하여 키워드와 타입으로 위치를 조회합니다.
 * 성공 시 위도/경도 좌표를 반환하고, 실패 시 null을 반환합니다.
 */
/**
 * @description
 * 위도/경도 좌표를 나타내는 기본 타입입니다.
 */
export type PlaceLatLng = { lat: number; lng: number };

/**
 * @description
 * Google Places Text Search API를 사용하여 키워드와 타입으로 첫 번째 검색 결과의 좌표를 조회합니다.
 * 성공 시 `{ lat, lng }`를 반환하고, 결과가 없거나 위치를 확인할 수 없으면 `null`을 반환합니다.
 * 내부적으로 콜백 기반 `textSearch`를 Promise로 래핑하여 `async/await`로 사용할 수 있게 합니다.
 *
 * @param keyword - 검색할 키워드(예: 대학교명, 주소, 역명)
 * @param placeType - Google Places `type` (예: `university`, `establishment`, `transit_station`)
 * @returns 좌표 `PlaceLatLng` 또는 `null`
 * @example
 * ```ts
 * const coords = await getPlaceFromKeyword('서울대학교', 'university');
 * if (coords) {
 *   // coords.lat, coords.lng 사용
 * }
 * ```
 */
export const getPlaceFromKeyword = async (keyword: string, placeType: string): Promise<PlaceLatLng | null> => {
  try {
    if (!window.google?.maps?.places) {
      console.error('Google Maps API or Places library not loaded');
      return null;
    }

    const service = new window.google.maps.places.PlacesService(document.createElement('div'));

    const request: google.maps.places.TextSearchRequest = {
      query: keyword,
      location: new window.google.maps.LatLng(37.566826, 126.9786567),
      radius: 50_000,
      type: placeType,
    };

    const result = await new Promise<PlaceLatLng | null>((resolve) => {
      service.textSearch(request, (results, status) => {
        const isOk = status === window.google.maps.places.PlacesServiceStatus.OK;
        const hasResults = Boolean(results && results.length > 0);

        if (!isOk || !hasResults) {
          console.warn(`Place not found for keyword: ${keyword}, status: ${status}`);
          resolve(null);
          return;
        }

        if (!results) {
          resolve(null);
          return;
        }
        const place = results[0];
        const location = place.geometry?.location;
        if (!location) {
          console.warn(`Location not found for keyword: ${keyword}`);
          resolve(null);
          return;
        }

        resolve({ lat: location.lat(), lng: location.lng() });
      });
    });

    return result;
  } catch (error) {
    console.error('Failed to fetch place data:', error);
    return null;
  }
};

/**
 * @description 옵션 배열을 Map 객체로 변환하는 유틸 함수
 * @example
 * const options = [
 *   { id: 'A', label: 'Option A' },
 *   { id: 'B', label: 'Option B' },
 * ] as const;
 * const map = createMapFromOptions(options);
 * // map: { A: 'Option A', B: 'Option B' }
 */
export const createMapFromOptions = <T extends readonly { id: string; label: string }[]>(
  options: T,
): { readonly [K in T[number]['id']]: Extract<T[number], { id: K }>['label'] } => {
  return options.reduce(
    (acc, option) => {
      acc[option.id] = option.label;
      return acc;
    },
    {} as Record<string, string>,
  ) as { readonly [K in T[number]['id']]: Extract<T[number], { id: K }>['label'] };
};
